/**
 * The workflow canvas's floating panels, as AmoregGTM's canvas has them:
 * the Steps panel top left (drag a step onto the chart, or press one to add
 * it after the selected step), the zoom stack bottom right (in, out, fit,
 * and Tidy while editing), and the card that follows the pointer while a
 * step is dragged out of the panel.
 *
 * @module components/workflows/canvas/panels
 */
import type { WorkflowNodeType } from "@amoreg/contracts";
import { type KeyboardEvent, type PointerEvent as ReactPointerEvent, type ReactNode, type Ref } from "react";
import { createPortal } from "react-dom";

import { cn } from "~/lib/utils";
import { Button } from "../../ui/button";
import { STEP_KINDS } from "../workflowCanvas.logic";
import { DragIcon, ExpandIcon, FitViewIcon, LayoutIcon, ZoomInIcon, ZoomOutIcon } from "./icons";
import { KINDS, kindClass, PALETTE } from "./kinds";

/** GTM's floating canvas panel: 12px radius, its own surface, a popover's shadow. */
export const PANEL_CLASS =
  "absolute top-4 left-4 z-20 flex max-h-[calc(100%-32px)] w-[min(320px,calc(100%-32px))] flex-col overflow-clip rounded-lg bg-canvas-panel shadow-canvas-popover";

/** GTM's glass pill and card, floating over the canvas. */
export const GLASS_PILL =
  "flex h-9 items-center gap-2.5 rounded-full bg-canvas-popover pr-1.5 pl-3.5 text-[12px] leading-5 font-medium shadow-canvas-popover backdrop-blur-[12px]";
export const GLASS_CARD = "rounded-lg bg-canvas-popover shadow-canvas-popover backdrop-blur-[12px]";

const RAISED = "bg-canvas-surface shadow-canvas-raised hover:bg-canvas-surface/80";

function Collapse({ open, children }: { readonly open: boolean; readonly children: ReactNode }) {
  return (
    <div
      data-open={open || undefined}
      inert={!open}
      className="grid min-h-0 grid-rows-[0fr] transition-[grid-template-rows] duration-300 ease-power3-in-out data-open:grid-rows-[1fr] motion-reduce:transition-none"
    >
      <div className="flex min-h-0 flex-col overflow-hidden">{children}</div>
    </div>
  );
}

export function Palette({
  ref,
  open,
  draggingKind,
  onToggle,
  onItemPointerDown,
  onAdd,
}: {
  readonly ref?: Ref<HTMLElement>;
  readonly open: boolean;
  readonly draggingKind: WorkflowNodeType | null;
  readonly onToggle: () => void;
  readonly onItemPointerDown: (kind: WorkflowNodeType, event: ReactPointerEvent) => void;
  readonly onAdd: (kind: WorkflowNodeType) => void;
}) {
  return (
    <aside
      ref={ref}
      aria-label="Steps"
      data-canvas-overlay
      data-workflow-palette=""
      onPointerDown={(event) => event.stopPropagation()}
      className={PANEL_CLASS}
    >
      {/* The whole header toggles the panel, not just the icon at its end. */}
      <button
        type="button"
        aria-expanded={open}
        aria-label={open ? "Collapse steps" : "Expand steps"}
        onClick={onToggle}
        data-workflow-add-step=""
        className="group/steps flex h-[52px] w-full shrink-0 cursor-pointer items-center justify-between px-[18px] py-2.5 text-left outline-none hover:bg-accent/40 focus-visible:bg-accent/40"
      >
        <span className="text-[14px] leading-6 font-medium text-foreground">Steps</span>
        <span className="flex size-7 items-center justify-center rounded-md group-hover/steps:bg-accent">
          <ExpandIcon className="size-[18px] text-muted-foreground" />
        </span>
      </button>
      <Collapse open={open}>
        <div className="h-px shrink-0 bg-(--canvas-line)" />
        <div className="min-h-0 overflow-y-auto px-5 py-[18px]">
          <div className="flex flex-col gap-4">
            {PALETTE.map((group) => (
              <section key={group.label} aria-label={group.label} className="flex flex-col gap-0.5">
                <div className="flex h-8 items-center py-1.5">
                  <span className="text-[12px] leading-6 font-medium text-muted-foreground uppercase">{group.label}</span>
                </div>
                <ul className="flex flex-col gap-0.5">
                  {group.kinds.map((kind) => (
                    <PaletteItem key={kind} kind={kind} dragging={draggingKind === kind} onPointerDown={onItemPointerDown} onAdd={onAdd} />
                  ))}
                </ul>
              </section>
            ))}
            <p className="text-[12px] leading-5 text-faint">
              Several links out of a step run side by side; a step with several links in waits for all of them.
            </p>
          </div>
        </div>
      </Collapse>
    </aside>
  );
}

function PaletteItem({
  kind,
  dragging,
  onPointerDown,
  onAdd,
}: {
  readonly kind: WorkflowNodeType;
  readonly dragging: boolean;
  readonly onPointerDown: (kind: WorkflowNodeType, event: ReactPointerEvent) => void;
  readonly onAdd: (kind: WorkflowNodeType) => void;
}) {
  const meta = KINDS[kind];
  const Icon = meta.icon;
  const description = STEP_KINDS.find((entry) => entry.type === kind)?.description;
  const onKeyDown = (event: KeyboardEvent) => {
    if (event.key !== "Enter" && event.key !== " ") return;
    event.preventDefault();
    onAdd(kind);
  };
  return (
    <li
      role="button"
      tabIndex={0}
      aria-label={`Add ${meta.label}`}
      title={description}
      data-workflow-add={kind}
      data-dragging={dragging || undefined}
      onPointerDown={(event) => onPointerDown(kind, event)}
      onKeyDown={onKeyDown}
      className={cn(
        kindClass(kind),
        "group/item relative flex w-full cursor-grab touch-none items-center rounded-md px-1 py-1.5 transition-[background-color,box-shadow,opacity] duration-150 ease-power3-in-out outline-none select-none hover:bg-foreground/3 hover:shadow-[0_0_0_1px_var(--canvas-line)] focus-visible:bg-foreground/3 focus-visible:shadow-[0_0_0_1px_color-mix(in_srgb,var(--ring)_60%,transparent)] active:cursor-grabbing data-dragging:opacity-50",
      )}
    >
      <span className="flex shrink-0 items-center pl-1">
        <Icon aria-hidden className="size-[18px] text-kind" />
      </span>
      <span className="flex min-w-0 flex-1 items-center pr-4 pl-2 text-[14px] leading-6 font-medium text-foreground">{meta.label}</span>
      <span className="flex items-center justify-end pr-0.5 opacity-0 transition-opacity duration-150 ease-power3-in-out group-hover/item:opacity-100 group-focus-visible/item:opacity-100">
        <DragIcon className="size-4 text-muted-foreground" />
      </span>
    </li>
  );
}

/** GTM's zoom stack: 32px raised buttons, 10px apart, bottom right; Tidy below them while editing. */
export function CanvasControls({
  canZoomIn,
  canZoomOut,
  onZoomIn,
  onZoomOut,
  onFit,
  onTidy,
}: {
  readonly canZoomIn: boolean;
  readonly canZoomOut: boolean;
  readonly onZoomIn: () => void;
  readonly onZoomOut: () => void;
  readonly onFit: () => void;
  readonly onTidy: (() => void) | null;
}) {
  return (
    <div
      role="toolbar"
      aria-label="Canvas zoom"
      data-canvas-overlay
      onPointerDown={(event) => event.stopPropagation()}
      className="absolute right-0 bottom-0 z-20 flex flex-col gap-2.5 p-4"
    >
      <Button variant="ghost" size="icon-sm" className={RAISED} aria-label="Zoom in" disabled={!canZoomIn} onClick={onZoomIn} data-workflow-zoom-in="">
        <ZoomInIcon className="size-[18px]" />
      </Button>
      <Button variant="ghost" size="icon-sm" className={RAISED} aria-label="Zoom out" disabled={!canZoomOut} onClick={onZoomOut} data-workflow-zoom-out="">
        <ZoomOutIcon className="size-[18px]" />
      </Button>
      <Button variant="ghost" size="icon-sm" className={RAISED} aria-label="Fit the chart to the screen" onClick={onFit} data-workflow-fit="">
        <FitViewIcon className="size-[18px]" />
      </Button>
      {onTidy ? (
        <Button variant="ghost" size="icon-sm" className={RAISED} aria-label="Tidy the chart" title="Tidy" onClick={onTidy} data-workflow-tidy="">
          <LayoutIcon className="size-[18px]" />
        </Button>
      ) : null}
    </div>
  );
}

/** The card following the pointer while a step is dragged out of the Steps panel. */
export function Ghost({ kind, x, y, snapped }: { readonly kind: WorkflowNodeType; readonly x: number; readonly y: number; readonly snapped: boolean }) {
  const meta = KINDS[kind];
  const Icon = meta.icon;
  // On the page itself, so no transformed or filtered ancestor can shift where `fixed` puts it; with the
  // canvas's own colours (`.workflow-canvas` holds them), which outside the canvas would be missing.
  return createPortal(
    <div aria-hidden style={{ translate: `${x}px ${y}px` }} className="workflow-canvas pointer-events-none fixed top-0 left-0 z-50" data-workflow-ghost="">
      <div
        data-snapped={snapped || undefined}
        className={cn(
          kindClass(kind),
          "flex h-9 w-[280px] cursor-grabbing items-center rounded-md bg-canvas-surface/95 px-1 py-1.5 shadow-canvas-popover backdrop-blur-md transition-[opacity,scale] duration-150 ease-power3-out data-snapped:scale-[0.96] data-snapped:opacity-60 motion-reduce:transition-none starting:scale-[0.96] starting:opacity-0",
        )}
      >
        <span className="flex shrink-0 items-center pl-1">
          <Icon aria-hidden className="size-[18px] text-kind" />
        </span>
        <span className="flex min-w-0 flex-1 items-center pr-4 pl-2 text-[14px] leading-6 font-medium text-foreground">{meta.label}</span>
        <span className="flex items-center justify-end pr-0.5">
          <DragIcon className="size-4 text-muted-foreground" />
        </span>
      </div>
    </div>,
    document.body,
  );
}
